import { HttpApiError } from '@backmarket/http-api'
import { useHttpFetch } from '@backmarket/nuxt-module-http-v2/useHttpFetch'
import { useSWRAsyncData } from '@backmarket/nuxt-module-http-v2/useSWRAsyncData'
import { useLogger } from '@backmarket/nuxt-module-logger/useLogger'

import type { Favorite } from '~/api/favorites/types'

import { LOG_ERROR_MESSAGE } from '../Favorites.constants'

/**
 * Fetches the list of favorites for the authenticated user with SWR caching.
 *
 * @param options - Configuration options
 * @param options.onDataUpdated - Callback executed when data is updated
 * @returns Object with data, error, hasFetchedOnce, and other useSWRAsyncData properties
 */
export function useFetchFavorites({
  onDataUpdated,
}: {
  onDataUpdated?: (data: Favorite[] | null | undefined) => void
}) {
  const $httpFetch = useHttpFetch()
  const logger = useLogger()

  return useSWRAsyncData(
    'favorites',
    async () => {
      try {
        return await $httpFetch('/my-favorites/favorites')
      } catch (err) {
        const error = HttpApiError.fromAnyError(err as Error)
        const errorType = error.status >= 500 ? 'api_5xx' : 'api_4xx'

        logger.error(LOG_ERROR_MESSAGE.FETCH, {
          error,
          feature: 'favorites',
          favorite_action: 'fetch_list',
          api_endpoint: '/my-favorites/favorites',
          cache_strategy: 'swr',
          error_type: errorType,
          error_status: error.status,
          owners: ['bot-squad-lifecycle-front'],
        })

        return []
      }
    },
    {
      onDataUpdated,
      // Skip SSR to avoid network failures during server-side rendering.
      // This authenticated endpoint requires cookies which can fail intermittently
      // during SSR (TypeError: Load failed).
      server: false,
    },
  )
}
